import { Box, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { useQuery } from "@tanstack/react-query";
import useAPI from "api/hooks/useAPI";
import useColors from "api/hooks/useColors";
import LoadingIcon from "components/LoadingIcon";
import dayjs from "dayjs";

const InternalDashboardTrends = ({ fromDate, toDate, envId, trendPreset }) => {
  const colors = useColors();
  const { getInternalDashboardTrends } = useAPI();

  const today = dayjs();
  let from, to;
  if (trendPreset === "custom") {
    from = fromDate ? fromDate.format("YYYY-MM-DD") : null;
    to = toDate ? toDate.format("YYYY-MM-DD") : null;
  } else {
    const days = parseInt(trendPreset) || 7;
    from = today.subtract(days, "day").format("YYYY-MM-DD");
    to = today.format("YYYY-MM-DD");
  }

  const { data: trendsData, isLoading } = useQuery({
    queryKey: ["internalDashboardTrends", from, to, envId],
    queryFn: async () => {
      const result = await getInternalDashboardTrends(from, to, envId);
      return result.data;
    },
    enabled: !!from && !!to,
  });

  let chartData = null;
  if (trendsData?.data_points) {
    const dates = trendsData.data_points.map((dp) => dp.date);
    const customersCount = trendsData.data_points.map(
      (dp) => dp.customers_count
    );
    const uniqueTools = trendsData.data_points.map((dp) => dp.unique_tools);
    const totalTools = trendsData.data_points.map((dp) => dp.total_tools);
    const entitiesTotal = trendsData.data_points.map((dp) => dp.entities_total);
    const uniqueControls = trendsData.data_points.map(
      (dp) => dp.unique_controls
    );

    chartData = {
      dates,
      customersCount,
      uniqueTools,
      totalTools,
      entitiesTotal,
      uniqueControls,
    };
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <LoadingIcon />
      </Box>
    );
  }

  if (!chartData || chartData.dates.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No trend data available for the selected range
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <LineChart
        xAxis={[
          {
            data: chartData.dates,
            scaleType: "point",
            label: "Date",
          },
        ]}
        series={[
          {
            data: chartData.customersCount,
            label: "Customers",
            color: colors.primary_main,
          },
          {
            data: chartData.uniqueTools,
            label: "Unique Tools",
            color: colors.info_main,
          },
          {
            data: chartData.totalTools,
            label: "Total Tools",
            color: colors.info_dark,
          },
          {
            data: chartData.uniqueControls,
            label: "Unique Controls",
            color: colors.warning_main,
          },
          {
            data: chartData.entitiesTotal,
            label: "Total Entities",
            color: colors.secondary_main,
            yAxisKey: "rightAxis",
          },
        ]}
        yAxis={[{ id: "leftAxis" }, { id: "rightAxis", position: "right" }]}
        height={300}
        margin={{ left: 50, right: 50, top: 60, bottom: 50 }}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "top", horizontal: "center" },
            padding: { top: 0, bottom: 20 },
          },
        }}
      />
    </Box>
  );
};

export default InternalDashboardTrends;
